import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import "./FinancialPage.css";
import FinancialDetailsTopBar from "./Components/TopBar/TopBar";
import FinancialDashboard from "./FinancialDashboard/GeneralFinancialDashboard";
import GeneralInvoicesPage from "./Invoice/GeneralInvoicePage";
import GeneralBillingPage from "./Billing/GeneralBilling";
import GeneralExpenses from "./Expenses/GeneralExpenses";
import GeneralTransactionsTable from "./Transactions/GeneralTransactionsTable";
import useCurrentEmployee from "@frontend/AppSettings/hooks/useCurrentEmployee";

type ActivePage = "dashboard" | "invoice" | "billing" | "expenses" | "transactions";

const FinancialsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentEmployee = useCurrentEmployee();
  const permissionsResolved = currentEmployee !== null;
  const permishId = currentEmployee?.permish_id ?? null;
  const permishIdNum = typeof permishId === 'string' ? parseInt(permishId, 10) : permishId;
  const isTimesheetOnlyUser = permishIdNum === 2 || permishIdNum === 3;

  const getActivePageFromPath = useCallback((): ActivePage => {
    const path = location.pathname;
    if (path === '/financials' || path === '/financials/' || path.startsWith('/financials/dashboard')) {
      return isTimesheetOnlyUser ? 'billing' : 'dashboard';
    }
    if (path.startsWith('/financials/invoice')) return 'invoice';
    if (path.startsWith('/financials/billing')) return 'billing';
    if (path.startsWith('/financials/expenses')) return 'expenses';
    if (path.startsWith('/financials/transactions')) return 'transactions';
    return isTimesheetOnlyUser ? 'billing' : 'dashboard';
  }, [location.pathname, isTimesheetOnlyUser]);

  const activePage = useMemo(() => getActivePageFromPath(), [getActivePageFromPath]);

  useEffect(() => {
    if (isTimesheetOnlyUser) {
      navigate('/financials/billing', { replace: true });
    }
  }, [isTimesheetOnlyUser, navigate]);

  const handleSetActivePage = useCallback(
    (page: string) => {
      const validPages: ActivePage[] = ["dashboard", "invoice", "billing", "expenses", "transactions"];
      if (!validPages.includes(page as ActivePage)) return;
      const route = page === 'dashboard' ? '/financials' : `/financials/${page}`;
      navigate(route, { replace: true });
    },
    [navigate]
  );

  const renderContent = () => {
    switch (activePage) {
      case 'invoice':
        return <GeneralInvoicesPage />;
      case 'billing':
        return <GeneralBillingPage />;
      case 'expenses':
        return <GeneralExpenses />;
      case 'transactions':
        return <GeneralTransactionsTable />;
      case 'dashboard':
      default:
        return <FinancialDashboard />;
    }
  };

  if (!permissionsResolved) {
    return (
      <div className="financial-details-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className="financial-details-page">
      {!isTimesheetOnlyUser && (
        <FinancialDetailsTopBar
          setActivePage={handleSetActivePage}
          activePage={activePage}
        />
      )}
      <div>
        <div className="FP-matter-content">{renderContent()}</div>
      </div>
    </div>
  );
};

export default FinancialsPage;
